import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getClientPortalBase, setClientTenant } from "@/lib/clientPortalPaths";
import { apiRequest } from "@/services/api";
import { clientAuthService } from "@/services/clientAuth.service/clientAuth.api";
import { AlertCircle, Loader2, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface MagicLinkResponse {
  accessToken: string;
  user: {
    _id: string;
    name: string;
    email: string;
    clientId: string;
    organizationId: string;
  };
  ticketId?: string;
  callId?: string;
}

export default function MagicLinkLanding() {
  const { token, tenant } = useParams<{ token: string; tenant?: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [pendingTarget, setPendingTarget] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submittingEmail, setSubmittingEmail] = useState(false);

  // SaaS multi-tenant — a URL sempre carrega a slug da organização (/:tenant/c/:token); fixa o
  // tenant atual antes de navegar pro portal, senão o redirect final pode cair na organização
  // errada se o navegador já tiver outro tenant salvo de uma sessão anterior.
  useEffect(() => {
    if (tenant) setClientTenant(tenant);
  }, [tenant]);

  useEffect(() => {
    if (!token) {
      setError("Link inválido ou expirado.");
      return;
    }

    async function resolve() {
      const [data, err] = await apiRequest<MagicLinkResponse>({
        loaderKey: "magicLink",
        method: "get",
        url: `/app/client-auth/magic/${token}`,
        ignoreError: true,
      });

      if (err || !data?.accessToken) {
        setError("Este link é inválido ou já expirou. Solicite um novo link.");
        return;
      }

      // Persist client token — same key the portal uses
      localStorage.setItem("client_token", data.accessToken);
      if (data.user) {
        localStorage.setItem("client_user", JSON.stringify(data.user));
      }
      localStorage.removeItem("client_impersonated");

      // Se o magic link foi gerado pra uma videochamada (nasce da conversa, pode não ter ticket ainda),
      // vai direto pra tela da chamada — senão, pro ticket normal.
      const base = getClientPortalBase();
      const target = data.callId ? `${base}/chamada/${data.callId}` : `${base}/tickets/${data.ticketId}`;

      // Contato que nasceu só com telefone via WhatsApp — sem e-mail, ele nunca recebe as
      // notificações de interação do ticket por e-mail. Pede antes de seguir pro portal.
      if (!data.user?.email) {
        setPendingTarget(target);
        return;
      }

      navigate(target, { replace: true });
    }

    void resolve();
  }, [token, navigate]);

  async function handleSubmitEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError("Digite um e-mail válido");
      return;
    }
    setSubmittingEmail(true);
    const [, err] = await clientAuthService.setEmail(email.trim());
    setSubmittingEmail(false);
    if (err) {
      setEmailError(err.msg || "Erro ao salvar o e-mail");
      return;
    }
    if (pendingTarget) navigate(pendingTarget, { replace: true });
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <h1 className="text-xl font-semibold">Link inválido</h1>
        <p className="max-w-sm text-muted-foreground text-sm">{error}</p>
      </div>
    );
  }

  if (pendingTarget) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-4 rounded-lg bg-card p-8 shadow-lg">
          <h1 className="text-xl font-semibold">Qual é o seu e-mail?</h1>
          <p className="text-sm text-muted-foreground">
            Precisamos do seu e-mail para te avisar sobre novidades e respostas neste chamado.
          </p>
          <form onSubmit={handleSubmitEmail} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                type="email"
                placeholder="voce@exemplo.com"
                className="pl-10"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {emailError && <p className="text-sm text-destructive">{emailError}</p>}
            <Button type="submit" className="w-full" disabled={submittingEmail}>
              {submittingEmail ? "Salvando..." : "Continuar"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">Autenticando...</p>
    </div>
  );
}
